import React, { useEffect, useRef, useState } from "react";
import { connect } from 'react-redux';
import Loading from "../Loading";
import { DEBUG } from "../../settings/global";
import { dispatchNotification } from "../../layouts/app/components/notificationSystem/notifications";
import Messages from "../Messages";

export const CardContext = React.createContext(undefined);

export function unfoldCard(id) {
    const element = document.getElementById(id);
    const isFolded = element?.dataset?.collapsed === 'true';

    if (!isFolded) return;

    element.setAttribute('data-external', 'true');
}

function CardAdvanced({
                          style,
                          className,
                          title,
                          children,
                          id, // saves collapsed state to local storage
                          collapsed, // sets collapsed state by default (before saving to local storage)
                          messages,
                          loading, // from parent
                          layoutDimensions, // redux
                          showMessages,
                          error,
                          warning,
                          success,
                          info,
                          disableCollapse,
                          darker,
                      }) {

    const storedStates = typeof window !== 'undefined' ? localStorage.getItem('blocks-state') : '';

    const foundBlockIndex = storedStates ? JSON.parse(storedStates).findIndex(block => block.id === id) : -1;
    const storedCollapsed = storedStates ? JSON.parse(storedStates)[foundBlockIndex]?.collapsed : collapsed;
    const [isCollapsed, setCollapsed] = useState(false);
    const [isHover, setHover] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // from children
    const [isUploading, setIsUploading] = useState(false); // from children
    const [recalc, setRecalc] = useState(false); // from children

    const collapse = (isCollapsed && !disableCollapse) ? ' cardAdvanced--collapsed' : '';

    const handleCollapse = () => {
        setCollapsed(!isCollapsed);
        // save state only for blocks with id provided
        if (!id) return;
        const stored = localStorage.getItem('blocks-state');
        let newStorage = [];
        if (stored) {

            const blocks = JSON.parse(stored);
            const foundIndex = JSON.parse(stored).findIndex(block => block.id === id);

            if (foundIndex !== -1) {
                newStorage = [
                    ...blocks.slice(0, foundIndex),
                    ...blocks.slice(foundIndex + 1),
                    ...[{
                        ...JSON.parse(stored)[foundIndex],
                        ...{collapsed: !isCollapsed}
                    }]
                ]
            } else {
                newStorage = [...JSON.parse(stored), ...[{id, collapsed: !isCollapsed}]]
            }
        } else {
            newStorage = [{id, collapsed: !isCollapsed}];
            if (!!DEBUG) dispatchNotification({
                message: 'I, the code, will try to remember your choice!',
                type: 'success',
            })
        }
        localStorage.setItem('blocks-state', JSON.stringify(newStorage))
    };

    useEffect(() => {
        if (!!id) {
            // subscribe to attribute change for external state update from unfoldCard() function
            const targetNode = document.getElementById(id);
            const observer = new MutationObserver((mutationList, observer) => {
                for (const mutation of mutationList) {
                    if (mutation.type === "attributes") {
                        const element = document.getElementById(id);
                        const isFolded = element?.dataset?.collapsed === 'true';
                        const isExternal = element?.dataset?.external === 'true';

                        if (isFolded && isExternal) {
                            handleCollapse();
                            element.setAttribute('data-external', 'false');
                        }
                    }
                }
            });

            observer?.observe(targetNode, {attributes: true, childList: false, subtree: false});

            return () => {observer?.disconnect()}
        }
    }, [isCollapsed]);

    useEffect(() => {
        setCollapsed(typeof storedCollapsed === 'boolean' ? storedCollapsed : !!collapsed)
    }, []);

    useEffect(() => {
        setCollapsed(disableCollapse ? false : typeof storedCollapsed === 'boolean' ? storedCollapsed : !!collapsed)
    }, [disableCollapse]);

    const header = useRef();
    const content = useRef();

    const handleHover = e => {
        if (!disableCollapse) setHover(header.current?.contains(e.target));
    }

    let height = useRef(undefined);

    const setHeight = () => {
        if (!isCollapsed) height.current = content.current?.offsetHeight;
    }

    useEffect(() => {
        document.addEventListener('mouseover', handleHover);
        setTimeout(setHeight, 0);
        return () => {document.removeEventListener('mouseover', handleHover);}
    }, [])

    useEffect(() => {
        setTimeout(
            setHeight,
            typeof recalc === 'number' ? recalc : 0
        );
        if (typeof recalc === 'number' || !!recalc) setTimeout(
            () => setRecalc(false),
            typeof recalc === 'number' ? recalc : 0
        );
    }, [layoutDimensions.height, layoutDimensions.width, recalc])

    let point = '';

    const _info = messages?.some(e => e.container === id && e.type === 'info');
    const _success = messages?.some(e => e.container === id && e.type === 'success');
    const _warning = messages?.some(e => e.container === id && e.type === 'warning');
    const _error = messages?.some(e => e.container === id && e.type === 'error');

    if (_info || info) point = ' cardAdvanced--info';
    if (_success || success) point = ' cardAdvanced--success';
    if (_warning || warning) point = ' cardAdvanced--warning';
    if (_error || error) point = ' cardAdvanced--error';

    const hover = isHover && !disableCollapse ? ' cardAdvanced--hover' : '';
    const dark = darker ? ' cardAdvanced--darker' : '';

    return (
        <div
            className={`cardAdvanced${dark}${collapse}${hover}${point}${className ? ' ' + className : ''}`}
            style={{
                ...style,
                ...(height ? {'--content-height': `${height?.current * 2 || 4000}px`} : {})
            }}
            data-collapsed={isCollapsed}
            data-hover={isHover}
            id={id}
        >
            <div
                onClick={!disableCollapse ? () => setTimeout(() => handleCollapse(), 100) : undefined}
                className='cardAdvanced__header'
                ref={header}
            >
                <div className='cardAdvanced__header__title'>{title}</div>
                {
                    !disableCollapse &&
                    <div className='cardAdvanced__header__icon'>
                        <i className='fas fa-chevron-up cardAdvanced__header__icon__arrow' aria-hidden='true'/>
                    </div>
                }
                {
                    (loading || isLoading) &&
                    <Loading
                        type='component-cover'
                        className='cardAdvanced__header__loading'
                        coverOpacity={0}
                        circleDiameter='2.8rem'
                        icon={{
                            faCode: `fas fa-chevron-${isUploading ? 'up' : 'down'}`,
                            animationType: isUploading ? 'top' : 'bottom'
                        }}
                    />
                }
            </div>
            {
                showMessages && !!point &&
                <div className='cardAdvanced__messages'><Messages messages={messages} container={id}/></div>
            }
            <div
                className='cardAdvanced__container'
                onClick={e => e.stopPropagation()}
            >
                <div className='cardAdvanced__container__content' ref={content}>
                    <CardContext.Provider
                        value={{
                            loading: setIsLoading,
                            upload: setIsUploading,
                            calculateHeight: (timeout) => {
                                setRecalc(timeout || true)
                            },
                        }}
                    >
                        {children}
                    </CardContext.Provider>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = state => ({
    layoutDimensions: state.UI.layoutDimensions,
});

export default connect(mapStateToProps)(CardAdvanced)